import { IconButton, Stack } from '@mui/material'
import styles from './index.module.scss'
import { ChangeEvent, ReactNode, useMemo, useRef, useState } from 'react'
import { DEFAULT_FILESIZE_LIMIT, getMimeTypes, getObjectURL } from './helper'
import UserAlert from '../AlertComponent/UserAlert'
import Compressor from 'compressorjs'
import DeleteIcon from '@mui/icons-material/Delete'
import { PresetExtensions, IconType, getPresetExtensions } from '.'

type FileType = {
    docTitle: string
    value: string | File
}

interface IFileUploadV2 {
    // fileName?: string;
    label?: ReactNode

    extensions?: string[] | PresetExtensions
    fileSizeLimitMb?: number | null
    compressionRatio?: number | null

    iconType?: IconType
    iconColor?: string

    disabled?: boolean
    canDelete?: boolean
    name?: string

    error?: boolean
    helperText?: string | ReactNode
    // file?: FileType
    // multiple?: boolean
}

interface ISingleFileUpload {
    multiple: false | undefined
    value: FileType | undefined

    onChange: (file: FileType | undefined) => void
}

interface IMultipleFileUpload {
    multiple: true
    value: FileType[]

    append: (e: FileType) => void
}

export const FileUpload = (
    props: IFileUploadV2 & (ISingleFileUpload | IMultipleFileUpload)
) => {
    const {
        extensions,
        // fileName,
        label,
        fileSizeLimitMb,
        disabled,
        compressionRatio,
        iconType,
        iconColor,
        canDelete,
        error,
        name,
        ...others
    } = props

    const [errorMsg, setErrorMsg] = useState<string>()
    const [fileSizeError, setFileSizeError] = useState<boolean>(false)
    const [fileTypeError, setFileTypeError] = useState<boolean>(false)

    const [dragActive, setDragActive] = useState(false)
    const inputRef = useRef<any>(null)
    const ext = useMemo(
        () =>
            typeof extensions === 'string'
                ? getPresetExtensions(extensions)
                : extensions,
        [extensions]
    )
    const accept = useMemo(() => getMimeTypes(ext).join(', '), [ext])

    const handleFileUpload = (uploadFile: File | null) => {
        setFileSizeError(false)
        setFileTypeError(false)
        if (uploadFile === null) return
        try {
            const fileSrc = uploadFile
            const fileName = fileSrc.name
            const fileType = fileSrc.type
            const fileSizeMb = fileSrc.size / 1024 / 1024 // MB

            if (!fileName.includes('.')) {
                throw new Error('File needs a valid extension')
            }
            const fileExt = fileName.split('.')[fileName.split('.').length - 1]
            if (!fileType) {
                throw new Error('Undefined file type')
            }
            if (
                fileSizeLimitMb !== null &&
                fileSizeMb > (fileSizeLimitMb || DEFAULT_FILESIZE_LIMIT)
            ) {
                setFileSizeError(true)
                return
            }

            if (ext && !ext.includes(fileExt.toLowerCase())) {
                setFileTypeError(true)
                return
            }

            const fileUrl = getObjectURL(fileSrc)
            if (!fileUrl) {
                throw new Error('Could not process the uploaded asset.')
            }

            const isCompressibleImage =
                ['png', 'jpg', 'jpeg'].includes(fileExt.toLowerCase()) &&
                fileType.startsWith('image/')

            if (compressionRatio === null || !isCompressibleImage) {
                if (others.multiple) {
                    others.append({ value: fileSrc, docTitle: fileName })
                } else {
                    others.onChange({ value: fileSrc, docTitle: fileName })
                }
                return
            } else {
                new Compressor(fileSrc, {
                    quality:
                        compressionRatio &&
                        compressionRatio <= 1 &&
                        compressionRatio > 0
                            ? compressionRatio
                            : 0.6,
                    success: (result) => {
                        if (others.multiple) {
                            others.append({
                                value: result as File,
                                docTitle: fileName,
                            })
                        } else {
                            others.onChange({
                                value: result as File,
                                docTitle: fileName,
                            })
                        }
                    },
                })
            }
        } catch (error: any) {
            setErrorMsg(error.message)
        }
    }

    const handleDrag = (e: React.DragEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        switch (e.type) {
            case 'dragenter':
            case 'dragover':
                setDragActive(true)
                break
            case 'dragleave':
                setDragActive(false)
        }
    }
    const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        const files = e.dataTransfer.files
        if (files === null) return
        for (let i = 0; i < files.length; i++) {
            handleFileUpload(e.dataTransfer.files.item(i))
        }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null) return
        for (let i = 0; i < e.target.files.length; i++) {
            handleFileUpload(e.target.files.item(i))
        }
    }

    return (
        <>
            <UserAlert
                open={errorMsg !== undefined}
                handleClose={() => setErrorMsg(undefined)}
                content={errorMsg}
            />
            {label}

            <Stack
                tabIndex={0}
                onDragLeave={handleDrag}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => {
                    if (disabled) return
                    inputRef.current?.click()
                }}
                onKeyDown={(e) => {
                    if (disabled) return
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        inputRef.current?.click?.()
                    }
                }}
                className={`${styles.labelContainer} ${dragActive ? styles['drag-active'] : ''} ${disabled ? styles['labelContainer-disabled'] : ''} ${!others.multiple && others.value ? styles['labelContainer--withfile'] : ''}`}
                style={{
                    borderColor: !!error ? styles.destructiveRed500 : undefined,
                }}
            >
                <input
                    name={name}
                    ref={inputRef}
                    type="file"
                    multiple={others.multiple}
                    hidden
                    accept={accept}
                    onChange={(e) => handleChange(e)}
                />
                <Stack>
                    {!others.multiple && others.value?.value !== undefined ? (
                        <Stack
                            alignItems={'center'}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                let newLink
                                if (typeof others.value!.value === 'string') {
                                    newLink = others.value!.value
                                } else {
                                    newLink = URL.createObjectURL(
                                        others.value!.value as any
                                    )
                                }
                                window && window.open(newLink, '_blank')
                            }}
                        >
                            {/* prettier-ignore */}
                            <svg className={styles['docPath--purple']} width="94" height="80" viewBox="0 0 94 80" fill="none" xmlns="http://www.w3.org/2000/svg" > <path fillRule="evenodd" clipRule="evenodd" d="M65.3461 34.907C75.3381 35.8191 84.2334 37.8319 91.6556 41.9175C101.412 47.288 77.6476 49.3342 76.3445 57.2067C74.9313 65.7447 99.0473 76.4089 85.4704 79.2857C73.2091 81.8837 61.6347 76.7524 49.3758 74.1504C33.6918 70.8215 6.56218 71.1857 0.970253 61.8487C-4.79101 52.229 16.4197 46.5749 29.9358 40.2854C40.6778 35.2868 51.8693 33.6767 65.3461 34.907Z" fill="#F5F5F5" /> <path className={styles['docPath--purple']} d="M34.9539 59.6099H59.046C63.9051 59.6099 66.4137 57.0561 66.4137 52.1744V31.947H49.4634C46.6384 31.947 45.2823 30.591 45.2823 27.7659V10.6348H34.9539C30.1174 10.6348 27.5862 13.1886 27.5862 18.0929V52.1744C27.5862 57.0561 30.1174 59.6099 34.9539 59.6099ZM49.8928 28.6473H66.1877C66.0521 27.6077 65.3063 26.6359 64.1311 25.4381L51.7687 12.9174C50.6386 11.7648 49.6216 10.9964 48.582 10.8608V27.3365C48.6046 28.2179 49.034 28.6473 49.8928 28.6473ZM47 53.7564C45.9603 53.7564 45.1015 52.9654 45.1015 51.9484V44.4902L45.2823 41.1228L43.5647 42.8856L41.7115 44.7614C41.3725 45.1004 40.8978 45.3265 40.4458 45.3265C39.4514 45.3265 38.7056 44.6484 38.7056 43.6766C38.7056 43.1342 38.9316 42.7274 39.3158 42.3658L45.5535 36.6479C46.0507 36.1959 46.4802 35.9925 47 35.9925C47.5424 35.9925 47.9492 36.1959 48.469 36.6479L54.6841 42.3658C55.0683 42.7274 55.2943 43.1342 55.2943 43.6766C55.2943 44.6484 54.5485 45.3265 53.5541 45.3265C53.0795 45.3265 52.6275 45.1004 52.2885 44.7614L50.4352 42.8856L48.7402 41.1228L48.8984 44.4902V51.9484C48.8984 52.9654 48.0396 53.7564 47 53.7564Z" fill="#A54D9D" /> </svg>
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                gap={1}
                            >
                                <p>
                                    {others.value.docTitle ||
                                        'Document Uploaded'}
                                </p>
                                {!!canDelete ? (
                                    <IconButton
                                        disabled={disabled}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            others.onChange(undefined)
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                ) : null}
                            </Stack>
                        </Stack>
                    ) : (
                        // Multiple Files
                        <>
                            {/* prettier-ignore */}
                            <svg width="94" height="80" viewBox="0 0 94 80" fill="none" xmlns="http://www.w3.org/2000/svg" > <path fillRule="evenodd" clipRule="evenodd" d="M65.3461 34.907C75.3381 35.8191 84.2334 37.8319 91.6556 41.9175C101.412 47.288 77.6476 49.3342 76.3445 57.2067C74.9313 65.7447 99.0473 76.4089 85.4704 79.2857C73.2091 81.8837 61.6347 76.7524 49.3758 74.1504C33.6918 70.8215 6.56218 71.1857 0.970253 61.8487C-4.79101 52.229 16.4197 46.5749 29.9358 40.2854C40.6778 35.2868 51.8693 33.6767 65.3461 34.907Z" fill="#F5F5F5" /> <path className={styles.docPath} style={{ fill: iconColor ? iconColor : '#0081A3', }} d="M27.5638 37.3937C28.3096 37.2807 29.0328 37.2129 29.8012 37.2129C30.547 37.2129 31.2928 37.2807 32.0612 37.4163V18.3641C32.0612 16.3075 33.1461 15.1549 35.3383 15.1549H44.9209V27.4495C44.9209 30.7266 46.5255 32.3086 49.78 32.3086H61.8938V51.8806C61.8938 53.9598 60.809 55.0898 58.6168 55.0898H44.2655C43.9943 56.7171 43.3841 58.2539 42.5479 59.6099H59.0236C63.8827 59.6099 66.3913 57.0561 66.3913 52.1744V31.6532C66.3913 28.4891 65.9845 27.0427 64.0183 25.0312L52.1982 13.0304C50.2772 11.0868 48.6952 10.6348 45.8475 10.6348H34.9315C30.095 10.6348 27.5638 13.1886 27.5638 18.0929V37.3937ZM48.8534 27.0201V15.7425L61.2836 28.3535H50.2094C49.2602 28.3535 48.8534 27.9467 48.8534 27.0201ZM29.8238 63.7232C36.1519 63.7232 41.463 58.4347 41.463 52.0614C41.463 45.6881 36.1971 40.4222 29.8238 40.4222C23.4505 40.4222 18.1846 45.6881 18.1846 52.0614C18.1846 58.4573 23.4505 63.7232 29.8238 63.7232ZM22.4335 52.0614C22.4335 51.1348 23.0663 50.5246 23.9929 50.5246H28.2644V46.2531C28.2644 45.3265 28.8746 44.6936 29.8238 44.6936C30.773 44.6936 31.3832 45.3265 31.3832 46.2531V50.5246H35.6547C36.5813 50.5246 37.1915 51.1348 37.1915 52.0614C37.1915 53.0106 36.5813 53.6208 35.6547 53.6208H31.3832V57.9149C31.3832 58.8415 30.773 59.4743 29.8238 59.4743C28.8746 59.4743 28.2644 58.8415 28.2644 57.9149V53.6208H23.9929C23.0663 53.6208 22.4335 53.0106 22.4335 52.0614Z" /> </svg>
                            <p>Click to choose file or drag and drop</p>
                            {fileSizeLimitMb === null ? null : (
                                <small>
                                    Max file size:{' '}
                                    {(
                                        fileSizeLimitMb ||
                                        DEFAULT_FILESIZE_LIMIT
                                    ).toString()}
                                    MB
                                    {extensions
                                        ? ` | ${ext?.join(', ').toLocaleUpperCase()} Only`
                                        : ''}
                                </small>
                            )}
                            {fileSizeError ? (
                                <small
                                    style={{
                                        color: styles.destructiveRed500,
                                    }}
                                >
                                    Size limit Exceeded
                                </small>
                            ) : null}
                            {fileTypeError ? (
                                <small
                                    style={{
                                        color: styles.destructiveRed500,
                                    }}
                                >
                                    File type not supported
                                </small>
                            ) : null}
                        </>
                    )}
                </Stack>
            </Stack>
        </>
    )
}

interface IUploadedFileList {
    files: FileType[]
    remove: (index: number) => void
}
export const UploadedFileList = ({ files, remove }: IUploadedFileList) => {
    if (!files || files.length === 0) return null
    return (
        <Stack gap={1.5}>
            {files.map((el, i) => (
                <Stack key={`empDocs--${i}`} className={styles.uploadSuccess}>
                    <small>
                        <a
                            href={
                                typeof el.value === 'string'
                                    ? el.value
                                    : URL.createObjectURL(el.value)
                            }
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            {el.docTitle || 'Document Uploaded'}
                        </a>
                    </small>
                    <IconButton onClick={() => remove(i)}>
                        {/* prettier-ignore */}
                        <svg width="32" height="31" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M11.477 24.6086H20.532C21.8419 24.6086 22.6069 23.9106 22.6642 22.6102L23.2666 8.79348H24.5862C25.0738 8.79348 25.4467 8.41101 25.4467 7.93293C25.4467 7.45484 25.0642 7.09149 24.5862 7.09149H20.4364V5.65723C20.4364 4.12736 19.4515 3.21899 17.8069 3.21899H14.1734C12.5288 3.21899 11.544 4.12736 11.544 5.65723V7.09149H7.41329C6.9352 7.09149 6.55273 7.4644 6.55273 7.93293C6.55273 8.42057 6.9352 8.79348 7.41329 8.79348H8.74237L9.35432 22.6102C9.40213 23.9106 10.1671 24.6086 11.477 24.6086ZM13.5328 5.74329C13.5328 5.31301 13.8292 5.03572 14.3073 5.03572H17.6826C18.1607 5.03572 18.4571 5.31301 18.4571 5.74329V7.09149H13.5328V5.74329ZM12.9304 21.6731C12.5479 21.6731 12.2802 21.4245 12.2706 21.0421L11.9838 10.8875C11.9742 10.505 12.242 10.2469 12.6436 10.2469C13.026 10.2469 13.2938 10.4955 13.3033 10.8779L13.5902 21.0325C13.6093 21.415 13.3416 21.6731 12.9304 21.6731ZM15.9997 21.6731C15.5981 21.6731 15.3304 21.4245 15.3304 21.0421V10.8779C15.3304 10.505 15.5981 10.2469 15.9997 10.2469C16.4013 10.2469 16.6786 10.505 16.6786 10.8779V21.0421C16.6786 21.4245 16.4013 21.6731 15.9997 21.6731ZM19.069 21.6827C18.6579 21.6827 18.3902 21.415 18.4093 21.0421L18.6961 10.8779C18.7057 10.4955 18.9734 10.2469 19.3559 10.2469C19.7575 10.2469 20.0252 10.505 20.0156 10.8875L19.7288 21.0421C19.7192 21.4245 19.4515 21.6827 19.069 21.6827Z" fill="#555555" /> </svg>
                    </IconButton>
                </Stack>
            ))}
        </Stack>
    )
}
